/**
 * A single saved resource on the My Team → Resources tab.
 * Mirrors the card in CuratedResourcesSection: the title links to the resource
 * detail page, and the bookmark button (outside the link) unsaves it.
 */

import { Link } from 'react-router-dom';
import { BookOpen, Video, Bookmark } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import type { FavoriteResource } from '../types';

type SavedResourceCardProps = {
  favorite: FavoriteResource;
  onRemove: (resourceId: string) => void;
  isRemoving: boolean;
};

export function SavedResourceCard({ favorite, onRemove, isRemoving }: SavedResourceCardProps) {
  const resource = favorite.resource;
  const isVideo = resource.type?.name?.toLowerCase() === 'video';

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between">
        <Link
          to={`/resource-hub-detail/${encodeURIComponent(resource.seoUrl)}`}
          className="flex items-start gap-4 flex-1 group"
        >
          <div className="w-12 h-12 rounded bg-[#026857]/10 flex items-center justify-center flex-shrink-0">
            {isVideo ? (
              <Video className="w-6 h-6 text-[#026857]" />
            ) : (
              <BookOpen className="w-6 h-6 text-[#026857]" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg text-gray-900 mb-1 group-hover:underline">{resource.title}</h3>
            {resource.seoDescription && (
              <p className="text-sm text-gray-600 mb-2">{resource.seoDescription}</p>
            )}
            {resource.type?.name && <Badge variant="outline">{resource.type.name}</Badge>}
          </div>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Remove from saved"
          onClick={() => onRemove(resource.id)}
          disabled={isRemoving}
        >
          <Bookmark aria-hidden="true" className="w-4 h-4 fill-[#026857] text-[#026857]" />
        </Button>
      </div>
    </Card>
  );
}
